// Translate an agent-produced `import_prefill_result` payload into the
// QmState shape used by <QmWizard>. Mirrors `prefillQmForm` from the legacy
// `web/public/app.js` (sections A–G + repeatable Personen / Referenzen).

import type { QmForm, QmPerson, QmReferenz, QmState } from "./state";

type Raw = Record<string, unknown>;

function pickString(...candidates: unknown[]): string | undefined {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s) return s;
  }
  return undefined;
}

function pickAny(...candidates: unknown[]): unknown {
  for (const c of candidates) {
    if (c != null && c !== "") return c;
  }
  return undefined;
}

function asObject(value: unknown): Raw {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Raw;
  }
  return {};
}

function toStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const cleaned = value
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter((s) => s.length > 0);
  return cleaned.length > 0 ? cleaned : undefined;
}

export function applyQmPrefill(prev: QmState, data: Record<string, unknown>): QmState {
  const meta = asObject(data.meta);
  const stamm = asObject(data.stammdaten);
  const beratungsfelder = asObject(data.beratungsfelder);
  const zielgruppen = asObject(data.zielgruppen);
  const team = asObject(data.team_qualifikation);
  const personalInfra = asObject(data.personal_infrastruktur);
  const beratungsprozess = asObject(data.beratungsprozess);
  const qualitaetsmanagement = asObject(data.qualitaetsmanagement);

  const next: Partial<QmForm> = {};

  const websiteUrl = pickString(meta.websiteUrl, meta.website_url, stamm.website_url, stamm.websiteUrl);
  if (websiteUrl) next.websiteUrl = websiteUrl;

  // Section A – Stammdaten
  const unternehmensname = pickString(stamm.unternehmensname);
  if (unternehmensname) next.unternehmensname = unternehmensname;
  const sitz = pickString(stamm.sitz_adresse, stamm.sitzAdresse);
  if (sitz) next.sitzAdresse = sitz;
  const gruendung = pickString(stamm.gruendung);
  if (gruendung) next.gruendung = gruendung;
  const geschaeftsfuehrung = pickString(stamm.geschaeftsfuehrung);
  if (geschaeftsfuehrung) next.geschaeftsfuehrung = geschaeftsfuehrung;
  const mitarbeitende = pickString(stamm.mitarbeitende);
  if (mitarbeitende) next.mitarbeitende = mitarbeitende;
  const kurzprofil = pickString(stamm.kurzprofil);
  if (kurzprofil) next.kurzprofil = kurzprofil;

  // Section B – Beratungsfelder
  const kernfelder = toStringArray(beratungsfelder.kern_beratungsfelder);
  if (kernfelder) next.kernfelder = kernfelder;
  const umsetzung = toStringArray(beratungsfelder.ergaenzende_umsetzungsleistungen);
  if (umsetzung) next.umsetzung = umsetzung;
  const nichtAngeboten = pickString(beratungsfelder.nicht_angeboten);
  if (nichtAngeboten) next.nichtAngeboten = nichtAngeboten;

  // Section C – Zielgruppen
  const hauptzielgruppen = toStringArray(zielgruppen.hauptzielgruppen);
  if (hauptzielgruppen) next.zielgruppen = hauptzielgruppen;
  const branchenfokus = pickString(zielgruppen.branchenfokus);
  if (branchenfokus) next.branchenfokus = branchenfokus;
  const anlaesse = toStringArray(zielgruppen.typische_projektanlaesse);
  if (anlaesse) next.anlaesse = anlaesse;

  // Section D – Team & Referenzen
  const personenRaw = Array.isArray(team.schluesselpersonen) ? team.schluesselpersonen : null;
  if (personenRaw) {
    const personen: QmPerson[] = personenRaw
      .map((p) => asObject(p))
      .filter((p) => p.name_funktion || p.qualifikation || p.erfahrung || p.sprachen)
      .map((p) => ({
        name_funktion: pickString(p.name_funktion) ?? "",
        qualifikation: pickString(p.qualifikation) ?? "",
        erfahrung: pickString(p.erfahrung) ?? "",
        sprachen: pickString(p.sprachen) ?? "",
      }));
    if (personen.length > 0) next.personen = personen;
  }

  const referenzenRaw = Array.isArray(team.referenzen_projektbeispiele) ? team.referenzen_projektbeispiele : null;
  if (referenzenRaw) {
    const referenzen: QmReferenz[] = referenzenRaw
      .map((r) => asObject(r))
      .filter((r) => r.ziel || r.branche || r.zeitraum || r.rolle || r.ergebnis)
      .map((r) => ({
        ziel: pickString(r.ziel) ?? "",
        branche: pickString(r.branche) ?? "",
        zeitraum: pickString(r.zeitraum) ?? "",
        rolle: pickString(r.rolle) ?? "",
        ergebnis: pickString(r.ergebnis) ?? "",
      }));
    if (referenzen.length > 0) next.referenzen = referenzen;
  }

  // Section E – Personal & Infrastruktur
  const personalgewinnung = pickString(personalInfra.personalgewinnung);
  if (personalgewinnung) next.personalgewinnung = personalgewinnung;
  const weiterbildung = pickString(personalInfra.weiterbildung);
  if (weiterbildung) next.weiterbildung = weiterbildung;
  const kapazitaeten = pickString(personalInfra.kapazitaeten);
  if (kapazitaeten) next.kapazitaeten = kapazitaeten;
  const infrastruktur = pickString(personalInfra.infrastruktur_tools);
  if (infrastruktur) next.infrastrukturTools = infrastruktur;
  const netzwerke = pickString(personalInfra.netzwerke_partner);
  if (netzwerke) next.netzwerkePartner = netzwerke;

  // Section F – Beratungsprozess
  const erstkontakt = pickString(beratungsprozess.erstkontakt);
  if (erstkontakt) next.erstkontakt = erstkontakt;
  const bedarfsermittlung = pickString(beratungsprozess.bedarfsermittlung);
  if (bedarfsermittlung) next.bedarfsermittlung = bedarfsermittlung;
  const auftragsklarung = pickString(beratungsprozess.auftragsklarung);
  if (auftragsklarung) next.auftragsklarung = auftragsklarung;
  const durchfuehrung = pickString(beratungsprozess.durchfuehrung);
  if (durchfuehrung) next.durchfuehrung = durchfuehrung;
  const dokumentation = pickString(beratungsprozess.dokumentation);
  if (dokumentation) next.dokumentation = dokumentation;
  const feedbackNachbetreuung = pickString(beratungsprozess.feedback_nachbetreuung);
  if (feedbackNachbetreuung) next.feedbackNachbetreuung = feedbackNachbetreuung;

  // Section G – Qualitätsmanagement
  const qualitaetszieleRaw = pickAny(qualitaetsmanagement.qualitaetsziele);
  if (Array.isArray(qualitaetszieleRaw) && qualitaetszieleRaw.length > 0) {
    next.qualitaetsziele = qualitaetszieleRaw
      .map((z) => (z == null ? "" : String(z).trim()))
      .filter((s) => s.length > 0)
      .join("\n");
  } else {
    const qz = pickString(qualitaetsmanagement.qualitaetsziele);
    if (qz) next.qualitaetsziele = qz;
  }
  const interneKontrollen = pickString(qualitaetsmanagement.interne_kontrollen);
  if (interneKontrollen) next.interneKontrollen = interneKontrollen;
  const beschwerden = pickString(qualitaetsmanagement.beschwerden_abweichungen);
  if (beschwerden) next.beschwerdenAbweichungen = beschwerden;
  const kvp = pickString(qualitaetsmanagement.kvp_lessons_learned);
  if (kvp) next.kvpLessonsLearned = kvp;
  const risiken = pickString(qualitaetsmanagement.risiken_chancen);
  if (risiken) next.risikenChancen = risiken;
  const ueberpruefung = pickString(qualitaetsmanagement.interne_ueberpruefung);
  if (ueberpruefung) next.interneUeberpruefung = ueberpruefung;

  // Per legacy: jump to Step 2 with the form prefilled, unlocking the step.
  const maxReachedStep = (prev.maxReachedStep < 2 ? 2 : prev.maxReachedStep) as QmState["maxReachedStep"];

  return {
    ...prev,
    phase: "wizard",
    form: { ...prev.form, ...next },
    step: 2,
    maxReachedStep,
  };
}
